// Plaid /transactions/sync orchestrator. Mirrors the desktop's
// `initial_sync_with_retry` + cursor-pagination loop in
// src-tauri/src/plaid.rs.
//
// Two real-world wrinkles this handles:
//
// 1. Cursor pagination — Plaid's /transactions/sync returns at most
//    `count` (we pass 500) transactions per call. We thread next_cursor
//    back in until has_more is false. This is the normal case for
//    every subsequent sync.
//
// 2. Warm-up on fresh Items — when a user just linked their bank via
//    Plaid Link, Plaid spends 30-60 s pulling historical transactions
//    from the bank. During that window the first sync returns either
//      (a) HTTP 400 with error_code PRODUCT_NOT_READY, or
//      (b) HTTP 200 with empty added + has_more=false.
//    We retry both cases until either real data arrives or the warm-up
//    window expires (default 90 s). Non-fresh items never warm-up
//    retry — an empty response on a follow-up sync is legitimate ("no
//    new transactions").
//
// Output is the same JSON shape that loadPlaidJson in src/loaders/
// already consumes — `accounts` + `added` + `modified` + `removed`
// + `item_id` + `institution_name`. Caller hands the blob to
// processFiles() as a synthetic input file (same pattern as the
// SimpleFIN pull in main.ts).

import {
  transactionsSync,
  PlaidApiError,
  type PlaidCredentials,
  type PlaidApiAccount,
  type PlaidApiTransaction,
  type TransactionsSyncResponse,
} from "./plaid_client.js";
import type { PlaidItem } from "./plaid_storage.js";

/** Shape written into raw/<YYYY-MM>/plaid_*.json. Matches what
 *  loadPlaidJson + readPlaidRemovedIds expect. Kept inline rather
 *  than imported from the loader to make this module standalone. */
export interface PlaidSyncDoc {
  item_id: string;
  institution_name: string;
  accounts: PlaidApiAccount[];
  added: PlaidApiTransaction[];
  modified: PlaidApiTransaction[];
  removed: Array<{ transaction_id: string }>;
}

export interface SyncPullResult {
  /** JSON-stringified PlaidSyncDoc. Hand to processFiles() as a
   *  synthetic input. */
  jsonText: string;
  /** Suggested filename: plaid_<item_id>_<ISO timestamp>.json.
   *  Matches the Rust layer's convention (see root CLAUDE.md). */
  filename: string;
  addedCount: number;
  modifiedCount: number;
  removedCount: number;
  /** Cursor returned by the last page. Caller should write this back
   *  into the item via plaid_storage.updateItemAfterSync. */
  finalCursor: string;
  /** True if we hit at least one PRODUCT_NOT_READY / empty-fresh
   *  retry. UI uses this to show a "first-sync warm-up" message. */
  hadWarmUp: boolean;
  /** EVERY account Plaid surfaced during this sync — regardless of
   *  the user's selected_account_ids filter. UI uses this to refresh
   *  the per-item known_accounts list (so newly-added bank accounts
   *  appear as candidates in Manage Banks). */
  observedAccounts: PlaidApiAccount[];
  /** Counts of transactions Plaid sent that we dropped because their
   *  account_id wasn't in the user's selected set. Surfaced in the
   *  activity log so the user can spot a misconfigured filter (e.g.
   *  "expected 30 transactions, only 2 imported"). */
  filteredOutCount: number;
}

export interface SyncOptions {
  /** Total warm-up budget for a fresh item. Default 90 000 ms. */
  warmUpBudgetMs?: number;
  /** Delay between warm-up retries. Default 5 000 ms. */
  warmUpStepMs?: number;
  /** Injectable sleep so tests can run without wall-clock waits. */
  sleep?: (ms: number) => Promise<void>;
  /** Injectable now() so tests can drive the warm-up deadline. */
  now?: () => number;
  /** Page size for /transactions/sync. Plaid's max is 500. */
  pageSize?: number;
}

const DEFAULT_WARMUP_BUDGET_MS = 90_000;
const DEFAULT_WARMUP_STEP_MS = 5_000;
const DEFAULT_PAGE_SIZE = 500;

function defaultSleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** Page through /transactions/sync until has_more is false, with
 *  warm-up retries on a fresh item. Returns the accumulated payload
 *  + the final cursor. Does NOT mutate vault state — caller is
 *  responsible for plaid_storage.updateItemAfterSync. */
export async function pullPlaidSync(
  creds: PlaidCredentials,
  item: PlaidItem,
  options: SyncOptions = {},
): Promise<SyncPullResult> {
  const warmUpBudget = options.warmUpBudgetMs ?? DEFAULT_WARMUP_BUDGET_MS;
  const warmUpStep = options.warmUpStepMs ?? DEFAULT_WARMUP_STEP_MS;
  const sleep = options.sleep ?? defaultSleep;
  const now = options.now ?? Date.now;
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;

  // Fresh = we have never had a successful sync against this item yet.
  // `last_sync === null` is the right signal — `last_cursor === ""`
  // would also fire for the "Reset & re-pull" flow, which empties the
  // cursor on an already-warm bank. Warm-up retry is for Plaid's
  // historical-data-not-ready window on a *brand-new* Item; cursor
  // resets on established items don't hit PRODUCT_NOT_READY (Plaid has
  // the data already), and an empty response there is legitimate.
  const isFresh = item.last_sync === null;
  const warmUpDeadline = now() + warmUpBudget;

  // Account filter: null = include everything. An empty array would
  // mean "include nothing" — the orchestrator UI rejects that case
  // before we reach here, but defensively respect it if it lands.
  const filterIds = item.selected_account_ids;
  const filterSet = filterIds == null ? null : new Set(filterIds);
  const isAccepted = (accountId: string): boolean =>
    filterSet === null ? true : filterSet.has(accountId);

  let cursor = item.last_cursor;
  let hadWarmUp = false;
  // observedAccounts tracks everything Plaid showed us, even if not
  // selected — so the UI can surface "new accounts available".
  const observedAccountMap = new Map<string, PlaidApiAccount>();
  // accountMap (written to the synthetic JSON) only keeps the
  // selected accounts.
  const accountMap = new Map<string, PlaidApiAccount>();
  const added: PlaidApiTransaction[] = [];
  const modified: PlaidApiTransaction[] = [];
  const removed: Array<{ transaction_id: string }> = [];
  let filteredOutCount = 0;

  while (true) {
    // Inner retry loop — only fires for fresh items, only on a) the
    // first page (cursor === item.last_cursor === "") and b) within
    // the warm-up budget. Budget is checked AFTER each call but
    // BEFORE the next attempt, so we never burn the queue past what
    // the budget allows.
    let resp: TransactionsSyncResponse | null = null;
    while (true) {
      let callErr: unknown = null;
      try {
        resp = await transactionsSync(creds, item.access_token, cursor, pageSize);
      } catch (err) {
        callErr = err;
      }

      // Classify the outcome.
      let warmUpEligible = false;
      if (callErr) {
        if (
          callErr instanceof PlaidApiError &&
          (callErr.body.error_code === "PRODUCT_NOT_READY" ||
            callErr.body.error_code === "PRODUCTS_NOT_READY") &&
          isFresh &&
          cursor === ""
        ) {
          warmUpEligible = true;
        } else {
          throw callErr;
        }
      } else if (
        resp &&
        isFresh &&
        cursor === "" &&
        resp.added.length === 0 &&
        resp.modified.length === 0 &&
        !resp.has_more
      ) {
        warmUpEligible = true;
      }

      if (!warmUpEligible) break;

      // Within warm-up window? Sleep + retry. Otherwise accept the
      // last outcome — for an empty 200 that means returning an
      // empty result; for a PRODUCT_NOT_READY error it means
      // re-throwing.
      if (now() >= warmUpDeadline) {
        if (callErr) throw callErr;
        break;
      }
      hadWarmUp = true;
      await sleep(warmUpStep);
    }

    if (!resp) {
      // Defensive — shouldn't be reachable. If callErr was set we
      // already either threw or broke out with resp populated.
      throw new Error("pullPlaidSync: response missing after retry loop");
    }

    for (const acc of resp.accounts) {
      observedAccountMap.set(acc.account_id, acc);
      if (isAccepted(acc.account_id)) accountMap.set(acc.account_id, acc);
    }
    for (const tx of resp.added) {
      if (isAccepted(tx.account_id)) added.push(tx);
      else filteredOutCount++;
    }
    for (const tx of resp.modified) {
      if (isAccepted(tx.account_id)) modified.push(tx);
      else filteredOutCount++;
    }
    for (const r of resp.removed) {
      if (!r.transaction_id) continue;
      // Plaid's `removed[]` entries sometimes include account_id and
      // sometimes don't. If absent, we can't filter — pass through.
      // If present and not in the selected set, skip; we never
      // imported it in the first place.
      if (r.account_id && !isAccepted(r.account_id)) {
        filteredOutCount++;
        continue;
      }
      removed.push({ transaction_id: r.transaction_id });
    }
    cursor = resp.next_cursor;
    if (!resp.has_more) break;
  }

  const doc: PlaidSyncDoc = {
    item_id: item.item_id,
    institution_name: item.institution_name,
    accounts: Array.from(accountMap.values()),
    added,
    modified,
    removed,
  };

  // Filename matches the Rust layer's convention so this JSON can be
  // dropped into a desktop user's raw/<YYYY-MM>/ folder and the
  // pipeline picks it up without modification.
  const isoSafe = new Date().toISOString().replace(/[:.]/g, "-");
  const filename = `plaid_${item.item_id}_${isoSafe}.json`;

  return {
    jsonText: JSON.stringify(doc),
    filename,
    addedCount: added.length,
    modifiedCount: modified.length,
    removedCount: removed.length,
    finalCursor: cursor,
    hadWarmUp,
    observedAccounts: Array.from(observedAccountMap.values()),
    filteredOutCount,
  };
}
